import wretch from 'wretch';

import { type LoadServerCxModuleOptions } from '@cx-modules/client';
import { configureErrorCatchers } from '@cx-modules/errors';
import { getEnvContextualizedSportsCatalogueEndpoint } from '@cx-modules/utils';

import { type MatchesCouponResponseData } from '../interfaces/matches-coupon.interface';
import { type LoadMatchesCouponArgs } from '../types';

export type MatchesCouponRequestParams = LoadMatchesCouponArgs;

export const fetchMatchesCouponData = async (
  dataApiEndpoint = getEnvContextualizedSportsCatalogueEndpoint(),
  params: MatchesCouponRequestParams,
  options?: LoadServerCxModuleOptions & { apiKey?: string },
): Promise<MatchesCouponResponseData | null> => {
  const { bettypeIds, countryCode, eventId, overrideBookies, repub, subdivision, subeventLimit } = params;
  const upperCountryCode = countryCode.toUpperCase();
  const upperRepub = repub.toUpperCase();
  const upperSubdivision = subdivision.toUpperCase();
  const url = new URL(`${dataApiEndpoint}/v1/subevent-group`);
  url.searchParams.append('eventId', eventId.toString());
  url.searchParams.append('betLimit', (2).toString());
  url.searchParams.append('subeventLimit', subeventLimit.toString());
  bettypeIds.forEach(number => url.searchParams.append('bettypeIds', number.toString()));
  overrideBookies?.forEach(code => url.searchParams.append('overrideBookies', code));

  try {
    const client = configureErrorCatchers(
      wretch(url.toString(), {
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          'country-code': upperCountryCode,
          repub: upperRepub,
          ...(upperSubdivision ? { 'subdivision-code': upperSubdivision } : {}),
          ...(options?.apiKey ? { 'api-key': options.apiKey } : {}),
          ...(options?.nocache && { 'Cache-Control': 'no-cache' }),
        },
      }),
      options,
    );

    return await client.get().json<MatchesCouponResponseData>();
  } catch (error) {
    options?.logger?.error('Failed to fetch matches coupon data:', error);
    throw error;
  }
};

export const getMatchesCouponCacheKey = ({
  bettypeIds,
  countryCode,
  eventId,
  overrideBookies,
  repub,
  subdivision,
  subeventLimit,
}: MatchesCouponRequestParams) => [
  'matches-coupon',
  repub.toUpperCase(),
  countryCode.toUpperCase(),
  subdivision.toUpperCase(),
  eventId.toString(),
  bettypeIds.sort((a, b) => a - b).join(','), // Sort to ensure consistent cache key
  subeventLimit.toString(),
  [...(overrideBookies ?? [])].sort().join(','), // Sort to ensure consistent cache key
];
